<template>
  <v-main class="pribta-queue-page">
    <div class="queue-shell">
      <div class="queue-cover" :style="{ backgroundImage: 'url(' + shopCover + ')' }" />

      <main class="queue-content-card">
        <div class="shop-logo">
          <img :src="shopLogo" alt="Pribta Tangerine Clinic">
        </div>

        <div class="language-switch" role="group" aria-label="Language">
          <button
            v-for="language in languages"
            :key="language.value"
            type="button"
            :class="{ active: selectedLanguage === language.value }"
            @click="selectedLanguage = language.value"
          >
            <img :src="language.image" :alt="language.label">
            <span>{{ language.label }}</span>
          </button>
        </div>

        <div v-if="loading && !queueDetail" class="state-card">
          <v-progress-circular indeterminate color="#ef7d22" size="42" width="4" />
          <p>{{ text.loading }}</p>
        </div>

        <div v-else-if="errorMessage" class="state-card error-state">
          <v-icon size="56" color="#e53935">mdi-alert-circle-outline</v-icon>
          <h2>{{ text.notFound }}</h2>
          <p>{{ errorMessage }}</p>
          <button type="button" class="refresh-button" @click="loadQueueDetail">
            <v-icon left color="white">mdi-refresh</v-icon>{{ text.tryAgain }}
          </button>
        </div>

        <template v-else-if="queueDetail">
          <h1 class="clinic-name">{{ shopName }}</h1>

          <section class="booking-detail">
            <div class="detail-row">
              <span>{{ text.date }}</span>
              <strong>{{ formattedDate }}</strong>
            </div>
            <div class="detail-row">
              <span>{{ text.service }}</span>
              <strong>{{ serviceName }}</strong>
            </div>
          </section>

          <section class="queue-card">
            <p class="queue-label">{{ text.yourQueue }}</p>
            <div class="queue-number">{{ queueDetail.queueNo || '-' }}</div>

            <div v-if="showWaitingAhead" class="waiting-line">
              <span>{{ text.waitingAhead }}</span>
              <strong>{{ queueDetail.waitingAhead || 0 }}</strong>
              <span>{{ text.queueUnit }}</span>
            </div>

            <div v-else class="queue-status" :class="statusTone">
              <h2>{{ statusTitle }}</h2>
              <p>{{ statusDescription }}</p>
              <div v-if="showCurrentPoint" class="current-point">
                <span>{{ text.currentPoint }}</span>
                <strong>{{ currentPointName }}</strong>
                <small v-if="queueDetail.counterNo">
                  {{ text.counter }} {{ queueDetail.counterNo }}
                </small>
              </div>
            </div>
          </section>

          <p class="no-show-note">* {{ text.noShowNote }}</p>
        </template>
      </main>
    </div>
  </v-main>
</template>

<script>
import axios from 'axios'
import { subscribePribta21QueueDetail } from '@/services/pribta21Realtime'

const TEXTS = {
  th: {
    pageTitle: 'ตรวจสอบสถานะคิว',
    clinic: 'คลินิก',
    live: 'อัปเดตสด',
    date: 'วันที่',
    branch: 'สาขา',
    service: 'ประเภทบริการ',
    yourQueue: 'หมายเลขคิวของคุณ',
    waitingAhead: 'มีคิวก่อนหน้าคุณ',
    queueUnit: 'คิว',
    currentPoint: 'จุดบริการปัจจุบัน',
    counter: 'เคาน์เตอร์',
    autoUpdate: 'หน้านี้จะอัปเดตสถานะคิวอัตโนมัติ',
    loading: 'กำลังตรวจสอบคิว...',
    notFound: 'ไม่พบข้อมูลคิว',
    tryAgain: 'ลองใหม่',
    invalidShop: 'ลิงก์นี้ไม่ใช่หน้าตรวจสอบคิวของ Pribta21',
    loadError: 'ไม่สามารถโหลดข้อมูลคิวได้ กรุณาลองใหม่อีกครั้ง',
    waiting: 'กรุณารอเรียกคิว',
    waitingDetail: 'โปรดเตรียมตัวให้พร้อมเมื่อใกล้ถึงคิวของคุณ',
    called: 'ถึงคิวของคุณแล้ว',
    calledDetail: 'กรุณาไปยังจุดบริการที่แสดงด้านล่าง',
    skipped: 'คิวของคุณถูกข้าม',
    skippedDetail: 'กรุณาติดต่อเจ้าหน้าที่เพื่อยืนยันคิวอีกครั้ง',
    forwarded: 'กำลังรอรับบริการ',
    forwardedDetail: 'กรุณารอเรียกคิวที่จุดบริการปัจจุบัน',
    completed: 'ให้บริการเรียบร้อยแล้ว',
    completedDetail: 'ขอบคุณที่ใช้บริการ',
    cancelled: 'คิวถูกยกเลิกแล้ว',
    cancelledDetail: 'หากต้องการสอบถามเพิ่มเติม กรุณาติดต่อเจ้าหน้าที่',
    noShowNote: 'ทางบริษัทขอสงวนสิทธิ์ในการข้ามคิวกรณีลูกค้าไม่แสดงตน'
  },
  en: {
    pageTitle: 'Queue status',
    clinic: 'Clinic',
    live: 'Live update',
    date: 'Date',
    branch: 'Branch',
    service: 'Service type',
    yourQueue: 'Your queue number',
    waitingAhead: 'Queues ahead of you',
    queueUnit: 'queues',
    currentPoint: 'Current service point',
    counter: 'Counter',
    autoUpdate: 'This page updates your queue status automatically.',
    loading: 'Checking your queue...',
    notFound: 'Queue not found',
    tryAgain: 'Try again',
    invalidShop: 'This link is not a Pribta21 queue-status link.',
    loadError: 'Unable to load your queue. Please try again.',
    waiting: 'Please wait for your queue',
    waitingDetail: 'Please be ready when your queue is approaching.',
    called: 'Your queue has been called',
    calledDetail: 'Please proceed to the service point shown below.',
    skipped: 'Your queue was skipped',
    skippedDetail: 'Please contact staff to confirm your queue.',
    forwarded: 'Waiting for service',
    forwardedDetail: 'Please wait to be called at the current service point.',
    completed: 'Service completed',
    completedDetail: 'Thank you for visiting us.',
    cancelled: 'Queue cancelled',
    cancelledDetail: 'Please contact staff if you need more information.',
    noShowNote: 'The company reserves the right to skip a queue when the customer is not present.'
  },
  ch: {
    pageTitle: '查询排队状态',
    clinic: '诊所',
    live: '实时更新',
    date: '日期',
    branch: '分店',
    service: '服务类型',
    yourQueue: '您的排队号码',
    waitingAhead: '您前面的队伍',
    queueUnit: '位',
    currentPoint: '当前服务点',
    counter: '柜台',
    autoUpdate: '此页面会自动更新排队状态',
    loading: '正在查询排队状态...',
    notFound: '找不到排队资料',
    tryAgain: '重试',
    invalidShop: '此链接不是 Pribta21 排队查询链接',
    loadError: '无法载入排队资料，请重试',
    waiting: '请等待叫号',
    waitingDetail: '当快到您的号码时，请做好准备',
    called: '已经叫到您的号码',
    calledDetail: '请前往下方显示的服务点',
    skipped: '您的号码已被跳过',
    skippedDetail: '请联系工作人员重新确认',
    forwarded: '正在等待服务',
    forwardedDetail: '请在当前服务点等候叫号',
    completed: '服务已完成',
    completedDetail: '感谢您的光临',
    cancelled: '排队已取消',
    cancelledDetail: '如需更多信息，请联系工作人员',
    noShowNote: '如顾客未到场，本公司保留跳过该号码的权利'
  }
}

export default {
  name: 'CheckQueuePribta21',
  data () {
    return {
      loading: false,
      queueDetail: null,
      errorMessage: '',
      shopName: 'Pribta Tangerine Clinic',
      shopLogo: require('@/assets/Thank.svg'),
      shopCover: require('@/assets/Cover2.jpg'),
      selectedLanguage: 'th',
      unsubscribeRealtime: null,
      refreshTimer: null,
      languages: [
        {
          value: 'th',
          label: 'TH',
          image: 'https://firebasestorage.googleapis.com/v0/b/betask-linked/o/picture-web%2Fflag-TH.png?alt=media&token=e4bd7ffa-aed9-47e3-9240-4dcb9d8d284e'
        },
        {
          value: 'en',
          label: 'EN',
          image: 'https://firebasestorage.googleapis.com/v0/b/betask-linked/o/picture-web%2Fflag-USA1.png?alt=media&token=a83dd820-f576-457a-8d08-1009cea9d70b'
        },
        {
          value: 'ch',
          label: 'CH',
          image: 'https://firebasestorage.googleapis.com/v0/b/betask-linked/o/event%2Fchaina.png?alt=media&token=2527d63c-cca8-45f2-85ba-d1539732c504'
        }
      ]
    }
  },
  computed: {
    shopId () {
      return String(this.$route.query.shopId || '')
    },
    bookNo () {
      return String(this.$route.query.bookNo || '')
    },
    text () {
      return TEXTS[this.selectedLanguage] || TEXTS.th
    },
    normalizedStatus () {
      return String((this.queueDetail && this.queueDetail.bookingStatus) || '').trim().toLowerCase()
    },
    branchName () {
      if (!this.queueDetail) return '-'
      return this.selectedLanguage === 'th'
        ? (this.queueDetail.branchNameTh || this.queueDetail.branchNameEn || '-')
        : (this.queueDetail.branchNameEn || this.queueDetail.branchNameTh || '-')
    },
    serviceName () {
      if (!this.queueDetail) return '-'
      if (this.selectedLanguage === 'ch') {
        return this.queueDetail.flowNameCh || this.queueDetail.flowNameEn || this.queueDetail.flowNameTh || '-'
      }
      return this.selectedLanguage === 'th'
        ? (this.queueDetail.flowNameTh || this.queueDetail.flowNameEn || '-')
        : (this.queueDetail.flowNameEn || this.queueDetail.flowNameTh || '-')
    },
    currentPointName () {
      const point = (this.queueDetail && this.queueDetail.currentPoint) || {}
      return this.selectedLanguage === 'th'
        ? (point.nameTh || point.nameEn || '-')
        : (point.nameEn || point.nameTh || '-')
    },
    formattedDate () {
      if (!this.queueDetail || !this.queueDetail.dueDate) return '-'
      const dateText = String(this.queueDetail.dueDate)
      const dateParts = dateText.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateParts) return dateParts[3] + '-' + dateParts[2] + '-' + dateParts[1]
      const date = new Date(dateText)
      if (Number.isNaN(date.getTime())) return String(this.queueDetail.dueDate).slice(0, 10)
      const pad = value => String(value).padStart(2, '0')
      return pad(date.getDate()) + '-' + pad(date.getMonth() + 1) + '-' + date.getFullYear()
    },
    statusTone () {
      if (['cancel', 'cancelled'].indexOf(this.normalizedStatus) !== -1) return 'tone-cancelled'
      if (this.normalizedStatus === 'closed') return 'tone-completed'
      if (this.normalizedStatus === 'confirm') return 'tone-called'
      if (this.normalizedStatus === 'skip') return 'tone-skipped'
      return 'tone-waiting'
    },
    statusIcon () {
      if (['cancel', 'cancelled'].indexOf(this.normalizedStatus) !== -1) return 'mdi-close-circle-outline'
      if (this.normalizedStatus === 'closed') return 'mdi-check-circle-outline'
      if (this.normalizedStatus === 'confirm') return 'mdi-bell-ring-outline'
      if (this.normalizedStatus === 'skip') return 'mdi-skip-next-circle-outline'
      return 'mdi-clock-outline'
    },
    statusTitle () {
      if (['cancel', 'cancelled'].indexOf(this.normalizedStatus) !== -1) return this.text.cancelled
      if (this.normalizedStatus === 'closed') return this.text.completed
      if (this.normalizedStatus === 'confirm') return this.text.called
      if (this.normalizedStatus === 'skip') return this.text.skipped
      if (this.normalizedStatus === 'forwarded') return this.text.forwarded
      return this.text.waiting
    },
    statusDescription () {
      if (['cancel', 'cancelled'].indexOf(this.normalizedStatus) !== -1) return this.text.cancelledDetail
      if (this.normalizedStatus === 'closed') return this.text.completedDetail
      if (this.normalizedStatus === 'confirm') return this.text.calledDetail
      if (this.normalizedStatus === 'skip') return this.text.skippedDetail
      if (this.normalizedStatus === 'forwarded') return this.text.forwardedDetail
      return this.text.waitingDetail
    },
    showWaitingAhead () {
      return ['checkin', 'checkedin', 'waiting', 'forwarded'].indexOf(this.normalizedStatus) !== -1
    },
    showCurrentPoint () {
      return ['cancel', 'cancelled', 'closed'].indexOf(this.normalizedStatus) === -1
    }
  },
  async mounted () {
    const queryLanguage = String(this.$route.query.languageSelect || '').toLowerCase()
    const languageMap = { '0': 'th', '1': 'en', '2': 'en', '3': 'ch', th: 'th', en: 'en', jp: 'en', ch: 'ch' }
    this.selectedLanguage = languageMap[queryLanguage] || 'th'

    if (this.shopId !== this.shopNameData.pribta21) {
      this.errorMessage = this.text.invalidShop
      return
    }
    if (!this.bookNo) {
      this.errorMessage = this.text.notFound
      return
    }

    await Promise.all([this.loadShop(), this.loadQueueDetail()])
    this.refreshTimer = setInterval(this.loadQueueDetail, 30000)
  },
  beforeDestroy () {
    this.stopRealtime()
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    async loadShop () {
      try {
        const response = await axios.get(this.DNS_IP + '/sys_shop/get?shopId=' + encodeURIComponent(this.shopId))
        const shops = Array.isArray(response.data) ? response.data : []
        if (!shops.length) return
        this.shopName = shops[0].shopNameEn || this.shopName
        this.shopLogo = shops[0].shopImge || this.shopLogo
        this.shopCover = shops[0].shopImageCover || this.shopCover
      } catch (error) {
        console.error('CheckQueuePribta21 load shop error:', error)
      }
    },
    async loadQueueDetail () {
      if (this.shopId !== this.shopNameData.pribta21 || !this.bookNo || this.loading) return
      try {
        this.loading = true
        this.errorMessage = ''
        const params = [
          'shopId=' + encodeURIComponent(this.shopId),
          'bookNo=' + encodeURIComponent(this.bookNo)
        ]
        const response = await axios.get(this.DNS_IP + '/booking/pribta21/queue-detail?' + params.join('&'))
        if (!response.data || response.data.status !== true) throw new Error('QUEUE_DETAIL_INVALID')
        const previousBranchID = this.queueDetail && this.queueDetail.masBranchID
        this.queueDetail = response.data
        if (!previousBranchID || String(previousBranchID) !== String(this.queueDetail.masBranchID)) {
          this.startRealtime()
        }
      } catch (error) {
        console.error('CheckQueuePribta21 load queue error:', error)
        if (!this.queueDetail) {
          this.errorMessage = error.response && error.response.status === 404
            ? this.text.notFound
            : this.text.loadError
        }
      } finally {
        this.loading = false
      }
    },
    startRealtime () {
      this.stopRealtime()
      if (!this.queueDetail || !this.queueDetail.masBranchID) return
      this.unsubscribeRealtime = subscribePribta21QueueDetail({
        firebase: this.$firebase,
        shopId: this.shopId,
        masBranchID: this.queueDetail.masBranchID,
        bookNo: this.bookNo,
        onEvent: () => this.loadQueueDetail(),
        onError: error => console.error('CheckQueuePribta21 realtime error:', error)
      })
    },
    stopRealtime () {
      if (typeof this.unsubscribeRealtime === 'function') this.unsubscribeRealtime()
      this.unsubscribeRealtime = null
    }
  }
}
</script>

<style scoped>
.pribta-queue-page {
  min-height: 100vh;
  background: #eef1f4;
  color: #4d4a5b;
  font-family: 'Kanit', sans-serif;
}

.queue-shell {
  width: 100%;
  max-width: 430px;
  min-height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 0 30px rgba(24, 35, 49, 0.14);
}

.queue-cover {
  height: 185px;
  background-color: #d8e6ea;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.queue-content-card {
  position: relative;
  min-height: calc(100vh - 145px);
  margin-top: -40px;
  padding: 74px 30px 34px;
  border-radius: 46px 46px 0 0;
  background: #ffffff;
}

.shop-logo {
  position: absolute;
  top: -57px;
  left: 50%;
  width: 114px;
  height: 114px;
  padding: 6px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(34, 48, 67, 0.2);
  transform: translateX(-50%);
}

.shop-logo img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.language-switch {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 19px;
}

.language-switch button {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 57px;
  height: 27px;
  padding: 3px 8px 3px 4px;
  border: 0;
  border-radius: 999px;
  background: #e8e8ec;
  color: #b2b0b7;
  font-size: 11px;
  font-weight: 700;
  transition: 0.15s ease;
}

.language-switch button img {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}

.language-switch button.active {
  background: #f58220;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(245, 130, 32, 0.28);
}

.state-card {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
}

.state-card p { margin: 0; color: #777482; }
.error-state h2 { margin: 0; color: #4d4a5b; }
.refresh-button { margin-top: 4px; padding: 9px 20px; border-radius: 999px; background: #f58220; color: white; font-weight: 700; }

.clinic-name {
  margin: 0 0 25px;
  color: #706c7b;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

.booking-detail {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  color: #55515e;
  font-size: 13px;
}

.detail-row span { flex: 0 0 auto; }
.detail-row strong { color: #696573; font-weight: 500; text-align: right; }

.queue-card {
  margin-top: 34px;
  text-align: center;
}

.queue-label { margin: 0; color: #65616f; font-size: 16px; font-weight: 700; }
.queue-number { margin: 0; color: #4c4858; font-size: 74px; font-weight: 800; line-height: 1.04; letter-spacing: -1px; }

.waiting-line {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-top: 3px;
  color: #686471;
  font-size: 12px;
}

.waiting-line strong {
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #4d4957;
  color: #ffffff;
  font-size: 12px;
  line-height: 20px;
}

.queue-status {
  margin-top: 8px;
  padding: 12px;
  border-radius: 12px;
  background: #f5f7f8;
}

.queue-status h2 { margin: 0; color: #4d4957; font-size: 17px; }
.queue-status p { margin: 4px 0 0; color: #777482; font-size: 12px; }
.tone-called { background: #eaf8fd; }
.tone-skipped { background: #fff3e9; }
.tone-completed { background: #ebf8ef; }
.tone-cancelled { background: #fceff0; }

.current-point {
  margin-top: 9px;
  display: flex;
  flex-direction: column;
  color: #666270;
  font-size: 12px;
}

.current-point strong { color: #4d4957; font-size: 14px; }
.current-point small { color: #777482; font-size: 12px; }

.no-show-note {
  margin: 48px 0 0;
  color: #696572;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}

@media (max-width: 520px) {
  .queue-shell { max-width: none; box-shadow: none; }
  .queue-cover { height: 165px; }
  .queue-content-card { min-height: calc(100vh - 125px); padding: 72px 25px 28px; }
  .no-show-note { margin-top: 42px; }
}
</style>



// WEBPACK FOOTER //
// src/components/Booking/CheckQueuePribta21.vue